import {
  kLabsMetadataVersion,
  LabsAudioResourceMetadata,
  LabsFileMetadata,
  LabsJsonResourceMetadata,
  LabsMidiResourceMetadata,
  ParseAndMigrateMetadata,
} from './labs.api';

export const isLabsFileMetadata = (
  value: unknown
): value is LabsFileMetadata => {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const cast = value as LabsFileMetadata;
  return (
    typeof cast.version === 'number' &&
    typeof cast.originalFilename === 'string' &&
    typeof cast.filenameOnDisk === 'string' &&
    typeof cast.dateAdded === 'string'
  );
};

// ------------------------------------ Audio -----------------------------------

export const isAudioResourceMetadata = (
  value: unknown
): value is LabsAudioResourceMetadata => {
  if (!isLabsFileMetadata(value)) {
    return false;
  }
  const cast = value as LabsAudioResourceMetadata;
  return (
    typeof cast.duration_s === 'number' &&
    typeof cast.sampleRate === 'number' &&
    typeof cast.numberOfChannels === 'number' &&
    (cast.spliceUuid === null || typeof cast.spliceUuid === 'string') &&
    (cast.bpm === null || typeof cast.bpm === 'number') &&
    (cast.chordType === null || typeof cast.chordType === 'string') &&
    (cast.key === null || typeof cast.key === 'string') &&
    (cast.type === null || typeof cast.type === 'string') &&
    (cast.artworkUrl === null || typeof cast.artworkUrl === 'string') &&
    Array.isArray(cast.tags) &&
    cast.tags.every((tag) => typeof tag === 'string')
  );
};

export interface V1AudioResourceMetadata {
  version: number;
  filename: string;
  duration_s: number;
  sampleRate: number;
  numberOfChannels: number;
}

export const isV1AudioResourceMetadata = (
  value: unknown
): value is V1AudioResourceMetadata => {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const cast = value as V1AudioResourceMetadata;
  return (
    cast.version === 1 &&
    typeof cast.filename === 'string' &&
    typeof cast.duration_s === 'number' &&
    typeof cast.sampleRate === 'number' &&
    typeof cast.numberOfChannels === 'number'
  );
};

export const migrateAudioV1ToV2 = (
  v1: V1AudioResourceMetadata,
  filenameOnDisk: string
): LabsAudioResourceMetadata => {
  return {
    version: kLabsMetadataVersion,
    originalFilename: v1.filename,
    filenameOnDisk,
    duration_s: v1.duration_s,
    sampleRate: v1.sampleRate,
    numberOfChannels: v1.numberOfChannels,
    dateAdded: new Date().toISOString(),
    spliceUuid: null,
    bpm: null,
    chordType: null,
    key: null,
    type: null,
    artworkUrl: null,
    tags: [],
  };
};

export const parseAndMigrateAudioMetadata: ParseAndMigrateMetadata<
  LabsAudioResourceMetadata
> = (
  parsed: unknown,
  filenameOnDisk: string
): LabsAudioResourceMetadata | Error => {
  if (isAudioResourceMetadata(parsed)) {
    return parsed;
  }
  if (isV1AudioResourceMetadata(parsed)) {
    return migrateAudioV1ToV2(parsed, filenameOnDisk);
  }
  return new Error('Metadata does not match any known version');
};

// ------------------------------------- MIDI -----------------------------------

export const isMidiResourceMetadata = (
  value: unknown
): value is LabsMidiResourceMetadata => {
  if (!isLabsFileMetadata(value)) {
    return false;
  }
  const cast = value as LabsMidiResourceMetadata;
  return (
    (cast.spliceUuid === null || typeof cast.spliceUuid === 'string') &&
    (cast.bpm === null || typeof cast.bpm === 'number') &&
    (cast.chordType === null || typeof cast.chordType === 'string') &&
    (cast.key === null || typeof cast.key === 'string') &&
    (cast.artworkUrl === null || typeof cast.artworkUrl === 'string') &&
    Array.isArray(cast.tags) &&
    cast.tags.every((tag) => typeof tag === 'string')
  );
};

export interface V1MidiResourceMetadata {
  version: number;
  filename: string;
  bpm: number;
}

export const isV1MidiResourceMetadata = (
  value: unknown
): value is V1MidiResourceMetadata => {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const cast = value as V1MidiResourceMetadata;
  return (
    cast.version === 1 &&
    typeof cast.filename === 'string' &&
    typeof cast.bpm === 'number'
  );
};

export const migrateMidiV1ToV2 = (
  v1: V1MidiResourceMetadata,
  filenameOnDisk: string
): LabsMidiResourceMetadata => {
  return {
    version: kLabsMetadataVersion,
    originalFilename: v1.filename,
    filenameOnDisk,
    dateAdded: new Date().toISOString(),
    spliceUuid: null,
    bpm: v1.bpm,
    chordType: null,
    key: null,
    tags: [],
    artworkUrl: null,
  };
};

export const parseAndMigrateMidiMetadata: ParseAndMigrateMetadata<
  LabsMidiResourceMetadata
> = (
  parsed: unknown,
  filenameOnDisk: string
): LabsMidiResourceMetadata | Error => {
  if (isMidiResourceMetadata(parsed)) {
    return parsed;
  }
  if (isV1MidiResourceMetadata(parsed)) {
    return migrateMidiV1ToV2(parsed, filenameOnDisk);
  }
  return new Error('Metadata does not match any known version');
};

// ------------------------------------- JSON -----------------------------------

export const isJsonResourceMetadata = (
  value: unknown
): value is LabsJsonResourceMetadata => {
  return isLabsFileMetadata(value);
};

export interface V1JsonResourceMetadata {
  version: number;
  filename: string;
  bpm: number;
}

export const isV1JsonResourceMetadata = (
  value: unknown
): value is V1JsonResourceMetadata => {
  if (value === null || typeof value !== 'object') {
    return false;
  }
  const cast = value as V1JsonResourceMetadata;
  return (
    cast.version === 1 &&
    typeof cast.filename === 'string' &&
    typeof cast.bpm === 'number'
  );
};

export const migrateJsonV1ToV2 = (
  v1: V1JsonResourceMetadata,
  filenameOnDisk: string
): LabsJsonResourceMetadata => {
  return {
    version: kLabsMetadataVersion,
    originalFilename: v1.filename,
    filenameOnDisk,
    dateAdded: new Date().toISOString(),
  };
};

export const parseAndMigrateJsonMetadata: ParseAndMigrateMetadata<
  LabsJsonResourceMetadata
> = (
  parsed: unknown,
  filenameOnDisk: string
): LabsJsonResourceMetadata | Error => {
  if (isJsonResourceMetadata(parsed)) {
    return parsed;
  }
  if (isV1JsonResourceMetadata(parsed)) {
    return migrateJsonV1ToV2(parsed, filenameOnDisk);
  }
  return new Error('Metadata does not match any known version');
};
